import {isMobileDevice} from "@/services/concurrency";

export const IMAGE_UPLOAD_RESTRICTIONS = {
    maxFileSize: 51200 * 1024,
    allowedExtensions: ['jpg', 'jpeg', 'jfif', 'png', 'heic', 'heif', 'webp'],
    allowedMimeTypes: ['image/jpeg', 'image/pjpeg', 'image/jfif', 'image/png', 'image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence', 'image/webp'],
    maxWidth: 12000,
    maxHeight: 12000
};

// Extensions that are handed to the system file picker (Uppy's `allowedFileTypes`
// end up in the <input accept="..."> attribute). iOS transcodes HEIC photos to
// JPEG on the fly *unless* HEIC is listed there, so on iOS we keep it out of the
// list and let the OS deliver JPEGs. Android and desktop browsers hand HEIC files
// through untouched, so there they must be allowed explicitly.
const IOS_PICKER_EXTENSIONS = ['jpg', 'jpeg', 'jfif', 'png', 'webp'];

const FILE_READ_MAX_ATTEMPTS = 3;
const FILE_READ_RETRY_DELAY_MS = 100;
const JPEG_INITIAL_SCAN_SIZE = 64 * 1024;
const JPEG_MAX_SCAN_SIZE = 512 * 1024;

function isIOS() {
    const userAgent = navigator.userAgent || '';
    const touchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1; // iPadOS

    return /iPhone|iPad|iPod/i.test(userAgent) || touchMac;
}

export function getUppyAllowedFileTypes() {
    const extensions = isIOS() ? IOS_PICKER_EXTENSIONS : IMAGE_UPLOAD_RESTRICTIONS.allowedExtensions;

    return extensions.map(extension => `.${extension}`);
}

// Parallel image work per device class. Applies to validation and compression.
export function getImageProcessingConcurrency() {
    return isMobileDevice() ? 2 : 4;
}

function getFileExtension(file) {
    const name = file && (file.name || (file.meta && file.meta.name));
    const extension = name && name.includes('.') ? name.split('.').pop() : '';

    return extension.toLowerCase();
}

function isFile(file) {
    const data = file && file.data;

    return Boolean(data && typeof data.size === 'number' && typeof data.slice === 'function');
}

function startsWithBytes(bytes, signature) {
    return signature.every((byte, index) => bytes[index] === byte);
}

function asciiAt(bytes, offset, length) {
    return String.fromCharCode(...bytes.slice(offset, offset + length));
}

function reportReadError(onReadError, error, details) {
    if(typeof onReadError !== 'function') {
        return;
    }

    try {
        onReadError(error, details);
    } catch(callbackError) {
        // A diagnostic callback must not change validation behavior.
    }
}

function isTransientReadError(error) {
    return error && (error.name === 'AbortError' || error.name === 'NotReadableError');
}

function waitForReadRetry() {
    return new Promise(resolve => setTimeout(resolve, FILE_READ_RETRY_DELAY_MS));
}

async function readArrayBuffer(data, end, onReadError, stage) {
    let lastError;

    for(let attempt = 1; attempt <= FILE_READ_MAX_ATTEMPTS; attempt++) {
        try {
            return await data.slice(0, end).arrayBuffer();
        } catch(error) {
            lastError = error;
            reportReadError(onReadError, error, {
                attempt,
                max_attempts: FILE_READ_MAX_ATTEMPTS,
                stage
            });

            if(!isTransientReadError(error) || attempt === FILE_READ_MAX_ATTEMPTS) {
                throw error;
            }

            await waitForReadRetry();
        }
    }

    throw lastError;
}

const HEIF_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'mif1', 'msf1'];

export async function detectFileMimeType(data, onReadError) {
    const header = new Uint8Array(await readArrayBuffer(data, 16, onReadError, 'mime'));

    if(startsWithBytes(header, [0xff, 0xd8, 0xff])) {
        return 'image/jpeg';
    }
    if(startsWithBytes(header, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
        return 'image/png';
    }
    if(header.length >= 12 && asciiAt(header, 4, 4) === 'ftyp' && HEIF_BRANDS.includes(asciiAt(header, 8, 4))) {
        return 'image/heic';
    }
    if(startsWithBytes(header, [0x52, 0x49, 0x46, 0x46]) && asciiAt(header, 8, 4) === 'WEBP') {
        return 'image/webp';
    }
    if(startsWithBytes(header, [0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) ||
        startsWithBytes(header, [0x47, 0x49, 0x46, 0x38, 0x39, 0x61])) {
        return 'image/gif';
    }
    if(startsWithBytes(header, [0x42, 0x4d])) {
        return 'image/bmp';
    }
    if(startsWithBytes(header, [0x49, 0x49, 0x2a, 0x00]) ||
        startsWithBytes(header, [0x4d, 0x4d, 0x00, 0x2a])) {
        return 'image/tiff';
    }

    return null;
}

function isAllowedMimeType(type) {
    return IMAGE_UPLOAD_RESTRICTIONS.allowedMimeTypes.includes(String(type || '').toLowerCase());
}

export function validateImageMetadata(file) {
    if(!file) {
        return 'image_validation_required';
    }

    if(!isFile(file)) {
        return 'image_validation_file';
    }

    if(!IMAGE_UPLOAD_RESTRICTIONS.allowedExtensions.includes(getFileExtension(file))) {
        return 'image_validation_mimes';
    }

    // Browsers (Windows, some Android pickers, cloud providers) regularly report
    // no MIME type or a generic one for perfectly fine photos. The extension check
    // above and the content check in validateImageFile() cover those cases.
    const type = String(file.type || '').toLowerCase();
    const isGenericType = type === '' || type === 'application/octet-stream';
    if(!isGenericType && !isAllowedMimeType(type)) {
        return 'image_validation_mimetypes';
    }

    const size = file.size == null ? file.data.size : file.size;
    if(size > IMAGE_UPLOAD_RESTRICTIONS.maxFileSize) {
        return 'image_validation_max';
    }

    return null;
}

/**
 * Reads the pixel dimensions from the file header without decoding the image.
 * Decoding a full-size photo needs ~50 MB of RAM; doing that for hundreds of
 * photos at once is what crashes mobile browsers. Returns null when the format
 * is not parsed here (the server checks dimensions anyway).
 */
function parseJpegDimensions(bytes) {
    let offset = 2;

    while(offset + 9 < bytes.byteLength) {
        if(bytes.getUint8(offset) !== 0xff) {
            return {dimensions: null, needsMore: false};
        }

        const marker = bytes.getUint8(offset + 1);
        if(marker === 0xff) {
            offset += 1;
            continue;
        }

        const isStandalone = marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7);
        if(isStandalone) {
            offset += 2;
            continue;
        }

        const isStartOfFrame = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
        if(isStartOfFrame) {
            return {
                dimensions: {
                    height: bytes.getUint16(offset + 5),
                    width: bytes.getUint16(offset + 7)
                },
                needsMore: false
            };
        }

        if(marker === 0xd9 || marker === 0xda) {
            return {dimensions: null, needsMore: false};
        }

        const segmentLength = bytes.getUint16(offset + 2);
        if(segmentLength < 2 || offset + 2 + segmentLength > bytes.byteLength) {
            return {dimensions: null, needsMore: true};
        }

        offset += 2 + segmentLength;
    }

    return {dimensions: null, needsMore: true};
}

async function readImageDimensionsFromHeader(data, mimeType, onReadError) {
    if(mimeType === 'image/png') {
        const bytes = new DataView(await readArrayBuffer(data, 24, onReadError, 'dimensions'));
        if(bytes.byteLength < 24) {
            return null;
        }

        return {
            width: bytes.getUint32(16),
            height: bytes.getUint32(20)
        };
    }

    if(mimeType === 'image/jpeg') {
        let scanSize = JPEG_INITIAL_SCAN_SIZE;

        while(true) {
            const bytes = new DataView(await readArrayBuffer(data, scanSize, onReadError, 'dimensions'));
            const result = parseJpegDimensions(bytes);
            if(result.dimensions || !result.needsMore || scanSize >= JPEG_MAX_SCAN_SIZE || data.size <= scanSize) {
                return result.dimensions;
            }

            scanSize = JPEG_MAX_SCAN_SIZE;
        }
    }

    return null;
}

export async function validateImageFile(file, onReadError) {
    const metadataError = validateImageMetadata(file);
    if(metadataError) {
        return metadataError;
    }

    let actualMimeType;
    try {
        actualMimeType = await detectFileMimeType(file.data, onReadError);
    } catch(error) {
        return 'image_validation_read';
    }

    if(!actualMimeType) {
        return 'image_validation_image';
    }

    if(!isAllowedMimeType(actualMimeType)) {
        return 'image_validation_mimetypes';
    }

    if(getFileExtension(file) === 'jfif' && actualMimeType !== 'image/jpeg') {
        return 'image_validation_mimetypes';
    }

    let dimensions = null;
    try {
        dimensions = await readImageDimensionsFromHeader(file.data, actualMimeType, onReadError);
    } catch(error) {
        return 'image_validation_read';
    }

    if(dimensions && (dimensions.width > IMAGE_UPLOAD_RESTRICTIONS.maxWidth ||
        dimensions.height > IMAGE_UPLOAD_RESTRICTIONS.maxHeight)) {
        return 'image_validation_dimensions';
    }

    return null;
}

/**
 * Compact description of a file for the upload log.
 */
export function describeFile(file) {
    if(!file) {
        return null;
    }

    return {
        name: file.name || (file.meta && file.meta.name) || null,
        type: file.type || null,
        size: file.size == null ? (file.data && file.data.size) : file.size,
        extension: getFileExtension(file) || null
    };
}

export function getImageValidationErrorKey(error, response) {
    let responseBody = null;

    if(response && typeof response.responseText === 'string' && response.responseText) {
        try {
            responseBody = JSON.parse(response.responseText);
        } catch(parseError) {
            responseBody = null;
        }
    }

    const responseText = responseBody ? JSON.stringify(responseBody) : '';
    const errorText = `${responseText} ${error && error.message ? error.message : ''}`.toLowerCase();

    if(errorText.includes('required')) {
        return 'image_validation_required';
    }
    if(errorText.includes('mimetypes') || errorText.includes('image/jpeg') || errorText.includes('image/png')) {
        return 'image_validation_mimetypes';
    }
    if(errorText.includes('mimes') || errorText.includes('jpg, jpeg, png')) {
        return 'image_validation_mimes';
    }
    if(errorText.includes('dimensions') || errorText.includes('width') || errorText.includes('height')) {
        return 'image_validation_dimensions';
    }
    if(errorText.includes('kilobytes') || errorText.includes('max:51200') || errorText.includes('maximum')) {
        return 'image_validation_max';
    }
    if(errorText.includes('must be a file')) {
        return 'image_validation_file';
    }
    if(errorText.includes('must be an image') || errorText.includes('valid image') || errorText.includes('image field') || errorText.includes('failed to load image')) {
        return 'image_validation_image';
    }

    return null;
}
